/**
 * Curated display options for a Site (Theme & settings → Display).
 *
 * Every key here is a runtime `settings2` key the site-admin API accepts on
 * `PATCH /settings` (ott-cms `constants/index.ts` defaultSettings, plus the
 * site-only keys watchlistEnabled / poweredByCastr / playerOpenImmediately /
 * liveOpenImmediately). Sites are English only: the runtime's
 * languageSwitcherEnabled / availableLocales are deliberately not offered. Defaults and
 * enums mirror the runtime exactly (lib/sites/admin/settings.ts) — it answers
 * 400/422 for unknown keys or values — so keep this file in step with
 * `constants/index.ts`, `types/index.ts` and `lib/display-settings.ts`.
 *
 * `cardOverlays`, `gridColumns` and `detailArtworkLayout` live outside
 * `defaultSettings` (lib/display-settings.ts); the site-admin API accepts them
 * since runtime v0.8.1 (`''` / `0` clears the row so the experience default
 * applies). Like every control they are only rendered when `GET /settings`
 * lists the key in `writableKeys` (see `isWritable`).
 */

/** Runtime defaults (ott-cms defaultSettings + lib/sites/admin/settings.ts). */
export const DISPLAY_DEFAULTS = Object.freeze({
	// Layout
	featuredLayout: 'contained',
	experience: 'catalog',
	categoryView: 'tabs',
	enableVerticalPoster: false,
	cardOverlays: '', // '' = follow the experience (lib/display-settings.ts)
	gridColumns: 0, // 0 = follow the experience
	detailArtworkLayout: '', // '' = follow the experience
	// Sections & navigation
	hideCategoriesSection: false,
	hideAllVideosFilter: false,
	hideCalendar: false,
	hideSearchBar: false,
	hideLive: false,
	disablePagination: false,
	newlyAddedContentsOnTop: true,
	defaultCategory: 0,
	watchlistEnabled: true,
	poweredByCastr: true,
	// Texts
	allVideosText: 'All Videos',
	homepageLoginText: 'Login',
	homepageSignupText: 'Sign up',
	loginPageLoginText: 'Login',
	loginPageSignupText: 'Sign up',
	signupPageSignupText: 'Sign up',
	websiteDescription: '',
	// Player — unlocked VOD on Sites opens the player straight away (runtime default flipped to true).
	playerOpenImmediately: true,
	liveOpenImmediately: true,
	playerAutoplay: false,
	enablePremiumOverlay: true,
	premiumOverlayText: 'Premium',
	enableSharing: true,
	enableBookmarkIcon: true,
	// No watermark keys: Sites play through the Castr embed player, which the runtime's
	// watermark never reaches; the site-admin API lists them under `deprecatedKeys`
	// and drops them from writes (Trello FCSwqbPY).
})

export const DISPLAY_KEYS = Object.keys(DISPLAY_DEFAULTS)

/**
 * Cards. Control types:
 *  - choice   : one of `options` (rendered as cards, thumbnail via `thumb`)
 *  - switch   : boolean; `invert` when the runtime key is a "hide…" flag but the
 *               UI states the positive ("Show search")
 *  - text     : string input (`maxlength`, optional `inputType`)
 *  - textarea : longer string
 *  - category : runtime category id (0 = none) — options come from the site
 *  `showWhen` hides a control until another draft key has the given value.
 */
export const DISPLAY_CARDS = [
	{
		id: 'layout',
		title: 'Layout',
		description: 'How the home page, categories and content cards are arranged.',
		controls: [
			{
				key: 'featuredLayout',
				type: 'choice',
				label: 'Featured banner',
				help: 'Immersive fills the top of the page edge to edge; Contained is a rounded card inside the page.',
				thumb: 'hero',
				options: [
					{ value: 'contained', text: 'Contained', hint: 'Rounded card' },
					{ value: 'immersive', text: 'Immersive', hint: 'Full-bleed hero' },
				],
			},
			{
				key: 'experience',
				type: 'choice',
				label: 'What is the site for?',
				help: 'Catalog is a library of shows and videos; Events puts dates, countdowns and pay-per-view first.',
				thumb: 'experience',
				options: [
					{ value: 'catalog', text: 'Catalog', hint: 'Library of videos' },
					{ value: 'events', text: 'Events', hint: 'Live & pay-per-view' },
				],
			},
			{
				key: 'categoryView',
				type: 'choice',
				label: 'Categories on the home page',
				help: 'Tabs or a dropdown filter one grid; Rows show one rail of content per category, like a streaming service, with the category tabs above the rails and — when the “All videos” tab is on — an “All videos” rail first.',
				thumb: 'categories',
				options: [
					{ value: 'tabs', text: 'Tabs' },
					{ value: 'dropdown', text: 'Dropdown' },
					{ value: 'rows', text: 'Rows' },
				],
			},
			{
				key: 'enableVerticalPoster',
				type: 'choice',
				label: 'Poster orientation',
				help: 'Landscape 16:9 thumbnails, or portrait 2:3 posters like a movie catalogue.',
				thumb: 'poster',
				options: [
					{ value: false, text: 'Landscape', hint: '16:9' },
					{ value: true, text: 'Portrait', hint: '2:3 posters' },
				],
			},
			{
				key: 'cardOverlays',
				type: 'choice',
				label: 'Text and badges over posters',
				help: 'Default follows the experience (Catalog: full, Events: none). Badges keeps only the small LIVE / premium pills; None moves the title under the art.',
				thumb: 'overlay',
				options: [
					{ value: '', text: 'Default' },
					{ value: 'full', text: 'Full', hint: 'Gradient + title' },
					{ value: 'badges', text: 'Badges only' },
					{ value: 'none', text: 'None', hint: 'Caption below' },
				],
			},
			{
				key: 'gridColumns',
				type: 'choice',
				label: 'Cards per row',
				help: 'Maximum cards per row on desktop. Default follows the experience and poster orientation.',
				thumb: 'columns',
				options: [
					{ value: 0, text: 'Default' },
					{ value: 3, text: '3' },
					{ value: 4, text: '4' },
					{ value: 5, text: '5' },
					{ value: 6, text: '6' },
				],
			},
			{
				key: 'detailArtworkLayout',
				type: 'choice',
				label: 'Content page artwork',
				help: 'Immersive puts the artwork behind the text as a backdrop; Contained shows it in a bordered panel beside the text.',
				thumb: 'detail',
				options: [
					{ value: '', text: 'Default' },
					{ value: 'immersive', text: 'Immersive', hint: 'Backdrop' },
					{ value: 'contained', text: 'Contained', hint: 'Side panel' },
				],
			},
		],
	},
	{
		id: 'sections',
		title: 'Sections & navigation',
		description: 'Which parts of the site viewers see and where they land.',
		controls: [
			{ key: 'hideCategoriesSection', type: 'switch', invert: true, label: 'Browse by category', help: 'Category tabs, dropdown or rows on the home page.' },
			{ key: 'hideAllVideosFilter', type: 'switch', invert: true, label: '“All videos” tab', help: 'A first tab that lists everything, before the categories. With Rows it is also the first rail, so content that is in no category still shows on the home page.' },
			{ key: 'defaultCategory', type: 'category', label: 'Category selected by default', help: 'Which category the home page opens on. “All videos” shows everything.', showWhen: { hideCategoriesSection: false } },
			{ key: 'newlyAddedContentsOnTop', type: 'switch', label: 'Newest content first', help: 'Sort grids by date added instead of manual order.' },
			{ key: 'hideLive', type: 'switch', invert: true, label: 'Live in the menu', help: 'A “Live” entry in the header that jumps to what is streaming now.' },
			{ key: 'hideCalendar', type: 'switch', invert: true, label: 'Calendar', help: 'The /calendar page with upcoming events.' },
			{ key: 'hideSearchBar', type: 'switch', invert: true, label: 'Search', help: 'Search box in the header.' },
			{ key: 'watchlistEnabled', type: 'switch', label: 'Watchlist', help: 'Hearts on cards, the Watchlist menu entry and /watchlist.' },
			{ key: 'disablePagination', type: 'switch', invert: true, label: 'Paginate long lists', help: 'Split long grids into pages instead of one endless list.' },
			{ key: 'poweredByCastr', type: 'switch', label: '“Powered by Castr” in the footer', help: 'Small muted link at the bottom of every page.' },
		],
	},
	{
		id: 'texts',
		title: 'Texts',
		description: 'Labels viewers read on buttons and tabs. Leave a field empty to use the default.',
		controls: [
			{ key: 'allVideosText', type: 'text', label: '“All videos” tab label', maxlength: 40 },
			{ key: 'homepageLoginText', type: 'text', label: 'Header “Login” button', maxlength: 40 },
			{ key: 'homepageSignupText', type: 'text', label: 'Header “Sign up” button', maxlength: 40 },
			{ key: 'loginPageLoginText', type: 'text', label: 'Login page button', maxlength: 40 },
			{ key: 'loginPageSignupText', type: 'text', label: 'Login page “Sign up” link', maxlength: 40 },
			{ key: 'signupPageSignupText', type: 'text', label: 'Sign-up page button', maxlength: 40 },
			{ key: 'websiteDescription', type: 'textarea', label: 'Site description', help: 'Used by search engines and link previews (about 160 characters).', maxlength: 300 },
		],
	},
	{
		id: 'player',
		title: 'Player',
		description: 'Playback behaviour, the paid badge and sharing.',
		controls: [
			{ key: 'playerOpenImmediately', type: 'switch', label: 'Open the player immediately (default)', help: 'Turn off to show the artwork hero with a Play button first.' },
			{ key: 'liveOpenImmediately', type: 'switch', label: 'Live on air opens the player', help: 'Live items that are on air open the player immediately.' },
			{ key: 'playerAutoplay', type: 'switch', label: 'Autoplay', help: 'Start playing as soon as the player is shown (muted where the browser requires it).' },
			{ key: 'enablePremiumOverlay', type: 'switch', label: 'Premium badge on paid content', help: 'A “Premium” badge on paid items the viewer has not unlocked — on the cards and on the content page alike.' },
			{ key: 'premiumOverlayText', type: 'text', label: 'Badge text', maxlength: 40, help: 'What the badge says, on cards and on the content page alike. Leave empty for “Premium”.', showWhen: { enablePremiumOverlay: true } },
			{ key: 'enableSharing', type: 'switch', label: 'Share button', help: 'Share to social networks from the content page.' },
			{ key: 'enableBookmarkIcon', type: 'switch', label: 'Watchlist button on cards', help: 'Heart icon on each card. Needs Watchlist enabled.' },
		],
	},
]

/** One-click looks. Only keys listed here change; everything else is kept. */
export const DISPLAY_PRESETS = [
	{
		id: 'cinematic',
		title: 'Cinematic',
		description: 'Full-bleed hero, one rail per category, four landscape cards per row with titles over the art, artwork as a backdrop on content pages — the streaming-service look.',
		thumb: 'preset-cinematic',
		values: { featuredLayout: 'immersive', categoryView: 'rows', enableVerticalPoster: false, cardOverlays: 'full', detailArtworkLayout: 'immersive', gridColumns: 4 },
	},
	{
		id: 'catalog',
		title: 'Catalog',
		description: 'Contained hero card, category tabs over one four-column grid, clean cards with badges only and a side artwork panel on content pages — the classic library layout.',
		thumb: 'preset-catalog',
		values: { featuredLayout: 'contained', categoryView: 'tabs', cardOverlays: 'badges', detailArtworkLayout: 'contained', gridColumns: 4 },
	},
]

/**
 * Keys only accepted by runtimes that advertise them (`writableKeys`, v0.8+).
 * When the response has no `writableKeys` at all (older runtime) these stay hidden.
 */
const ADVERTISED_ONLY_KEYS = ['cardOverlays', 'gridColumns', 'detailArtworkLayout', 'playerOpenImmediately', 'liveOpenImmediately']

/** True when the runtime lets the dashboard write this key. `null` list = unknown → trust the defaults. */
export function isWritable(key, writableKeys) {
	if (!Array.isArray(writableKeys)) return !ADVERTISED_ONLY_KEYS.includes(key)
	return writableKeys.includes(key)
}

const isObj = (v) => v !== null && typeof v === 'object'
export function sameValue(a, b) {
	if (Array.isArray(a) || Array.isArray(b)) {
		return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((v, i) => sameValue(v, b[i]))
	}
	if (isObj(a) || isObj(b)) return JSON.stringify(a) === JSON.stringify(b)
	return a === b
}

/**
 * The word the site puts on paid items (runtime `sitePremiumLabel`, Theme ›
 * Player): "Premium badge on paid content" on → its "Badge text", blank →
 * "Premium"; off → the plain "Paid". `settings` should be the values the
 * Theme page edits against (published ⊕ draft) so previews agree with it.
 */
export function premiumBadgeLabel(settings = {}) {
	const { enablePremiumOverlay, premiumOverlayText } = effectiveDisplayValues(settings)
	if (!enablePremiumOverlay) return 'Paid'
	return premiumOverlayText.trim() || DISPLAY_DEFAULTS.premiumOverlayText
}

/** Current effective values: saved (or KV-seeded) value → runtime default. */
export function effectiveDisplayValues(settings = {}) {
	const out = {}
	for (const key of DISPLAY_KEYS) {
		const v = settings[key]
		out[key] = v === undefined || v === null ? clone(DISPLAY_DEFAULTS[key]) : normalizeValue(key, v)
	}
	return out
}

function normalizeValue(key, v) {
	const def = DISPLAY_DEFAULTS[key]
	if (typeof def === 'boolean') return typeof v === 'boolean' ? v : v === 1 || v === '1' || v === 'true'
	if (typeof def === 'number') { const n = Number(v); return Number.isFinite(n) ? n : def }
	if (Array.isArray(def)) return Array.isArray(v) ? [...v] : clone(def)
	return typeof v === 'string' ? v : String(v ?? '')
}

export function clone(v) {
	return Array.isArray(v) ? [...v] : isObj(v) ? JSON.parse(JSON.stringify(v)) : v
}

/** Text keys where '' would leave a button without a label: blank falls back to the default. */
const LABEL_TEXT_KEYS = ['allVideosText', 'homepageLoginText', 'homepageSignupText', 'loginPageLoginText', 'loginPageSignupText', 'signupPageSignupText', 'premiumOverlayText']

/** Keys whose draft differs from the base, as a partial PATCH body. */
export function displayChanges(draft, base, writableKeys) {
	const patch = {}
	for (const key of DISPLAY_KEYS) {
		if (!isWritable(key, writableKeys)) continue
		let value = draft[key]
		if (typeof value === 'string') value = value.trim()
		if (LABEL_TEXT_KEYS.includes(key) && !value) value = DISPLAY_DEFAULTS[key]
		if (!sameValue(value, base[key])) patch[key] = clone(value)
	}
	return patch
}

/**
 * Inline validation, keyed by setting. Nothing in the display cards needs a
 * rule at the moment (the watermark image was the only one); kept so the Theme
 * page's error wiring stays in place for the next field that does.
 */
export function displayErrors() {
	return {}
}
